import * as React from 'react';
import { useState } from 'react';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Material UI Packages. */
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { randomId } from '@mui/x-data-grid-generator';
import LoadingButton from '@mui/lab/LoadingButton';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Dayjs Packages. */
import dayjs from 'dayjs';
import { operationDaySlot } from 'shared/ui/OperationPickersDay';

/** Internal Components. */
import { callAPI } from 'shared/api/callApi';
import { AutocompleteFieldTradePartner } from 'shared/ui/AutoCompleteForm';
import DragAndDrop from 'shared/ui/DragAndDrop/DragAndDrop';
import { PaperComponent } from 'shared/ui/DraggableDialog';
import InputField from 'shared/ui/InputForm';
import DialogHeader from 'shared/ui/DialogHeader';
import { handleDialogCloseWithDetection } from 'shared/lib/changeDetection';

const initState = (user) => ({
    il_trade_partner_id: null,
    quote_date: null,
    il_file: [],
    title: '',
    il_user_id: user.given_name + ' ' + user.family_name,
    il_quotation_file: [],
    modified_on: null,
});

const initValidation = () => ({
    title: false,
    tradePartner: false,
    quote_date: false,
    file: false,
});

export default function QuoteDialog(props) {
    /** Get props from QuoteArchive.js */
    const { dispatch, newOpen, setNewOpen, tradePartnerList, setRowUpdateTrigger, setActionLoading, setSearchData } =
        props;

    /** Destructuring values from useAuth0 hook. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** State for dialog inputs. */
    const [newQuoteForm, setNewQuoteForm] = useState(initState(user));
    /** State for validating user inputs. */
    const [validator, setValidator] = useState(initValidation);
    /** State for loading file upload phase. */
    const [fileUploadLoading, setFileUploadLoading] = useState(false);

    /** Handler for saving title, trade partner and quotation date input. */
    const handleNewQuoteFormChange = (key, newValue) => {
        setNewQuoteForm((prev) => ({
            ...prev,
            [key]: newValue,
        }));
    };

    /** Function for reset the states when dialog is closed. */
    const resetState = () => {
        setNewQuoteForm(initState(user));
        setNewOpen(false);
        setValidator(initValidation);
    };

    /** Handler for closing the dialog. */
    const handleClose = () => {
        handleDialogCloseWithDetection(null, newQuoteForm, initState(user), resetState);
    };

    /** Validate function for dialog inputs. */
    const isInputVerified = () => {
        // The Date range object allows input values up to 31 for the day, regardless of the month.
        // Therefore, an additional validation is performed using the `isValid` function from dayJS to ensure correctness.
        const validations = {
            title: !newQuoteForm['title'].trim(),
            tradePartner: !newQuoteForm['il_trade_partner_id'],
            quote_date: !newQuoteForm['quote_date'] || !newQuoteForm['quote_date'].isValid(),
            file: newQuoteForm['il_file'].length === 0,
        };

        setValidator((prev) => ({
            ...prev,
            ...validations,
        }));

        return Object.values(validations).includes(true) ? false : true;
    };

    /** Handler for saving new quotation by calling POST request.
     * - If success saving quotation, call GET request to load updated quotation list.
     */
    const handleSaveBtn = () => {
        // Make input validation before API request.
        if (!isInputVerified()) {
            return dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please complete the required input fields before proceeding.',
                    severity: 'warning',
                }),
            );
        }
        const postState = {
            ...newQuoteForm,
            id: randomId(),
            // The quote date must be stored exactly as the user picked it. Leaving the
            // dayjs value to JSON.stringify converts it to UTC, which saves the
            // previous day for users working from Korea.
            quote_date: newQuoteForm.quote_date ? newQuoteForm.quote_date.format('YYYY-MM-DD') : null,
            modified_on: dayjs(),
        };

        callAPI({
            method: 'post',
            module: 'quote_archive',
            component: 'create',
            getAccessTokenSilently,
            email: user.email,
            obj: JSON.stringify(postState),
            onInit: () => setActionLoading(true),
            onSuccess: (resp) => {
                resetState();
                setSearchData({ tradePartner: null, quotationDate: [null, null] });
                setRowUpdateTrigger((prev) => !prev);
                dispatch(setSnackbar({ open: true, message: 'Successfully Created', severity: 'success' }));
            },
            onError: () =>
                dispatch(
                    setSnackbar({ open: true, message: 'Someting went wrong. Please try again.', severity: 'error' }),
                ),
            onFinal: () => setActionLoading(false),
        });
    };
    console.log('newQuoteForm', newQuoteForm);
    console.log('loading', fileUploadLoading);
    console.log('validator', validator);
    return (
        <Dialog
            onClose={handleClose}
            open={newOpen}
            maxWidth={'1544px'}
            PaperProps={{ sx: { borderRadius: '18px' } }}
            PaperComponent={PaperComponent}
        >
            <DialogHeader
                cursor
                title="NEW QUOTATION FILE"
                onClose={handleClose}
                rightChildren={
                    <LoadingButton
                        onClick={handleSaveBtn}
                        loading={fileUploadLoading}
                        sx={{
                            color: '#00647e',
                            bgcolor: 'white',
                            width: '52px',
                            height: '30px',
                            borderRadius: '3px',
                            ':hover': { bgcolor: 'rgb(0,100,126)', color: 'white' },
                        }}
                    >
                        SAVE
                    </LoadingButton>
                }
            />
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 3 }}>
                    <DragAndDrop
                        moduleName="quote_archive"
                        uploadFileInfo={newQuoteForm.il_quotation_file}
                        validate={validator['file']}
                        setCalled={setNewQuoteForm}
                        maxFileSize={26214400}
                        setFileUploadLoading={setFileUploadLoading}
                        fileExtensions={['pdf', 'xls', 'xlsx', 'doc', 'docx', 'png', 'jpg']}
                    />
                    <InputField
                        label="Subject"
                        name="title"
                        value={newQuoteForm.title}
                        onChange={(e) => handleNewQuoteFormChange('title', e.target.value)}
                        error={validator['title']}
                        labelStyle={{ width: '108px' }}
                        inputStyle={{ fontSize: 13, width: '300px' }}
                        sx={{ mb: '12px' }}
                    />
                    <AutocompleteFieldTradePartner
                        key="Trade Partner"
                        label="Trade Partner"
                        name="tradePartner"
                        value={newQuoteForm.il_trade_partner_id}
                        options={tradePartnerList}
                        onChange={(event, newValue) => handleNewQuoteFormChange('il_trade_partner_id', newValue)}
                        error={validator['tradePartner']}
                        sx={{ display: 'flex', justifyContent: 'space-between', mb: '12px' }}
                        AutocompleteStyle={{ width: '69%' }}
                        disable
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '30px' }}>
                        <Typography
                            sx={{
                                display: 'inline',
                                color: '#003a52',
                                fontSize: 13,
                                fontWeight: 500,
                                width: '150px',
                            }}
                        >
                            Quote Date
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={newQuoteForm.quotationDate}
                                onChange={(newValue) => handleNewQuoteFormChange('quote_date', newValue)}
                                slots={operationDaySlot}
                                slotProps={{
                                    textField: {
                                        error: validator['quote_date'],
                                    },
                                }}
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-input': { px: 2, py: 0, height: '27px' },
                                }}
                            />
                        </LocalizationProvider>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
